import React, { useEffect, useState, useCallback } from 'react';
import { Plane, MapPin, Clock, AlertCircle, RefreshCcw, Wifi } from 'lucide-react';
import { FlightMap } from './FlightMap';
import { CockpitView } from './CockpitView';
import { LiveCameras } from './LiveCameras';
import AIRPORT_COORDS from '../lib/airportCoords';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
  } catch {
    return String(iso);
  }
};

// "2h 35m" / "23m" / "Arriving" — based on time to arrival timestamp
const timeToLanding = (iso) => {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    if (ms <= 0) return { label: 'Arriving / arrived', minutes: 0 };
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, minutes: totalMin };
  } catch {
    return null;
  }
};

const STATUS_THEMES = {
  active:    { label: 'In flight',  color: 'bg-emerald-500 animate-pulse', text: 'text-emerald-700', ring: 'ring-emerald-400/40' },
  'en-route':{ label: 'In flight',  color: 'bg-emerald-500 animate-pulse', text: 'text-emerald-700', ring: 'ring-emerald-400/40' },
  enroute:   { label: 'In flight',  color: 'bg-emerald-500 animate-pulse', text: 'text-emerald-700', ring: 'ring-emerald-400/40' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500',     text: 'text-blue-700',    ring: 'ring-blue-400/40' },
  landed:    { label: 'Landed',    color: 'bg-slate-600',    text: 'text-slate-700',   ring: 'ring-slate-400/40' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500',      text: 'text-red-700',     ring: 'ring-red-400/40' },
  diverted:  { label: 'Diverted',  color: 'bg-orange-500',   text: 'text-orange-700',  ring: 'ring-orange-400/40' },
  unknown:   { label: 'Not yet tracked', color: 'bg-slate-300', text: 'text-slate-600', ring: 'ring-slate-300/40' },
};

/**
 * Live flight tracker — drop in after a booking confirmation.
 *   <FlightTracker flightIata="PK786" />
 *
 * Polls /api/flight-tracker/<iata> at the interval returned by the backend
 * (5 min in-flight, 30 min otherwise) so we stay well under the AirLabs
 * 1,000-queries / month limit.
 */
export const FlightTracker = ({ flightIata, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/flight-tracker/${encodeURIComponent(flightIata)}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const d = await res.json();
      setData(d);
    } catch (e) {
      setError(e.message || 'Could not fetch status');
    } finally {
      setLoading(false);
    }
  }, [flightIata]);

  useEffect(() => {
    if (!flightIata) return;
    fetchStatus();
  }, [flightIata, fetchStatus]);

  // Auto-refresh after the server-suggested TTL
  useEffect(() => {
    if (!data?.next_refresh_seconds) return;
    const id = setTimeout(fetchStatus, data.next_refresh_seconds * 1000);
    return () => clearTimeout(id);
  }, [data, fetchStatus]);

  if (!flightIata) return null;

  if (loading && !data) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="flight-tracker-loading">
        <RefreshCcw className="h-4 w-4 animate-spin" /> Loading live status for {flightIata}…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-center gap-2" data-testid="flight-tracker-error">
        <AlertCircle className="h-4 w-4" /> Could not load live tracking for {flightIata}. Status: {error}
      </div>
    );
  }

  const theme = STATUS_THEMES[(data?.status || 'unknown').toLowerCase()] || STATUS_THEMES.unknown;
  const delay = data?.delay_min;
  const isDelayed = Number.isFinite(delay) && delay > 0;
  const inFlight = ['active', 'en-route', 'enroute', 'en route'].includes((data?.status || '').toLowerCase());
  const eta = timeToLanding(data?.arrival_estimated || data?.arrival_scheduled);
  // Progress percentage along the route (0% just took off, 100% landed)
  const depTime = data?.departure_actual || data?.departure_scheduled;
  const arrTime = data?.arrival_estimated || data?.arrival_scheduled;
  let progress = null;
  if (inFlight && depTime && arrTime) {
    const total = new Date(arrTime).getTime() - new Date(depTime).getTime();
    const done = Date.now() - new Date(depTime).getTime();
    if (total > 0) progress = Math.min(100, Math.max(0, (done / total) * 100));
  } else if ((data?.status || '').toLowerCase() === 'landed') {
    progress = 100;
  }

  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${compact ? '' : 'ring-1'} ${theme.ring}`}
      data-testid="flight-tracker"
    >
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plane className="h-5 w-5 -rotate-45" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Flight</div>
            <div className="font-bold text-lg leading-tight">{data?.flight_iata}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${theme.color}`}></span>
          <span className="text-xs font-semibold uppercase tracking-wider">{theme.label}</span>
          {inFlight && eta && (
            <span data-testid="flight-eta-chip" className="ml-2 text-[10px] font-bold bg-emerald-400/25 text-emerald-100 border border-emerald-300/40 rounded-full px-2 py-0.5">
              🛬 {eta.label} to landing
            </span>
          )}
          {isDelayed && (
            <span className="ml-2 text-[10px] font-bold bg-red-500/20 text-red-200 border border-red-400/40 rounded-full px-2 py-0.5">
              {delay}m delay
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 items-center gap-3 px-4 py-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-900">{data?.dep_iata || '—'}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Departure</div>
          <div className="text-[11px] text-slate-700 mt-1">{fmtTime(data?.departure_actual || data?.departure_scheduled)}</div>
          {(data?.dep_terminal || data?.dep_gate) && (
            <div className="text-[10px] text-slate-500 mt-0.5">
              {data?.dep_terminal && `T${data.dep_terminal} `}
              {data?.dep_gate && `· Gate ${data.dep_gate}`}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center text-slate-500" data-testid="flight-progress">
          <div className="w-full h-2 bg-slate-100 rounded-full relative overflow-hidden">
            {progress !== null && (
              <>
                <div
                  className={`h-full rounded-full ${inFlight ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' : 'bg-slate-500'} transition-all duration-1000`}
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-[14px]"
                  style={{ left: `${progress}%` }}
                  aria-hidden
                >
                  {inFlight ? '✈️' : '🛬'}
                </div>
              </>
            )}
          </div>
          {inFlight && eta && (
            <div className="mt-2 text-center">
              <div className="text-xs font-bold text-emerald-700">{eta.label}</div>
              <div className="text-[10px] text-slate-500">to landing</div>
            </div>
          )}
          {data?.lat && data?.lng && (
            <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {data.lat.toFixed(2)}°, {data.lng.toFixed(2)}°
            </div>
          )}
          {data?.speed_kmh && (
            <div className="text-[10px] text-slate-500 mt-0.5">{Math.round(data.speed_kmh)} km/h · {Math.round(data.alt_m || 0)} m</div>
          )}
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-slate-900">{data?.arr_iata || '—'}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Arrival</div>
          <div className="text-[11px] text-slate-700 mt-1">{fmtTime(data?.arrival_estimated || data?.arrival_scheduled)}</div>
          {(data?.arr_terminal || data?.arr_gate) && (
            <div className="text-[10px] text-slate-500 mt-0.5">
              {data?.arr_terminal && `T${data.arr_terminal} `}
              {data?.arr_gate && `· Gate ${data.arr_gate}`}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500 bg-slate-50/60">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {data?.cached ? 'Cached' : 'Live'} · auto-refresh in {Math.round((data?.next_refresh_seconds || 0) / 60)} min
        </span>
        <button
          type="button"
          onClick={fetchStatus}
          data-testid="flight-tracker-refresh"
          className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-medium"
        >
          <RefreshCcw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Cockpit-window preview — only when actually airborne */}
      {inFlight && (
        <div className="border-t border-slate-100">
          <CockpitView
            heading={data?.direction}
            altitudeM={data?.alt_m}
            speedKmh={data?.speed_kmh}
            lat={data?.lat}
            lng={data?.lng}
            etaLabel={eta?.label}
            depIata={data?.dep_iata}
            arrIata={data?.arr_iata}
            status={theme.label}
          />
        </div>
      )}


      {/* Live map */}
      <div className="border-t border-slate-100">
        <FlightMap
          depIata={data?.dep_iata}
          arrIata={data?.arr_iata}
          lat={data?.lat}
          lng={data?.lng}
          direction={data?.direction || 0}
          isActive={['active', 'en-route', 'en route', 'enroute'].includes((data?.status || '').toLowerCase())}
        />
      </div>

      {/* Live cameras — radar + tail-cam demo */}
      <div className="p-3 bg-slate-50/40 border-t border-slate-100">
        <LiveCameras
          lat={data?.lat}
          lng={data?.lng}
          depLat={data?.dep_iata ? (AIRPORT_COORDS[data.dep_iata.toUpperCase()]?.[0]) : null}
          depLng={data?.dep_iata ? (AIRPORT_COORDS[data.dep_iata.toUpperCase()]?.[1]) : null}
          aircraftIata={data?.aircraft_iata}
          airlineName={data?.airline_name}
        />
      </div>
    </div>
  );
};

export default FlightTracker;
